"use client";

/**
 * Enrollments tab table (Phase J · Task 56).
 *
 * Reuses the `Table` primitive in `variant="list"` from
 * `@/components/ui/table` so the row chrome matches the Tasks list and
 * other dashboard surfaces. Columns mirror what spec §13.1 calls out:
 *
 *   1. Profile (avatar + full name)
 *   2. Status pill (driven by `ENROLLMENT_STATUS_META`)
 *   3. Current step (resolves type+total from the sequence's current
 *      step set so the list view can render without an extra fetch
 *      per enrollment — the detail drawer in Task 58 honours each
 *      enrollment's pinned step set)
 *   4. Next due (relative time, or "Replied" / "Bounced" terminal)
 *   5. Trailing actions slot (Task 57 drops Pause/Resume/Cancel/Restart
 *      controls in here)
 *
 * Profile cells fetch the candidate via `useGetProfile(profile_id)`.
 * The query is only enabled per-row inside the `ProfileCell` so an
 * empty (or filtered-out) page never fires N+1 requests.
 *
 * Per AGENTS.md "Building New Features Not in Figma — CRITICAL", the
 * visual chrome is derived from `frontend/src/features/tasks/components
 * /list/table.tsx` and the `audit-log.tsx` row treatment. The cited
 * spec §2 nodes (28:55888 / 28:48809 / 28:50254 / 28:53685) document
 * the Phase I Preview surface, not this list — the list view itself is
 * intentionally a `variant="list"` table per spec §3 Existing
 * Components Reused row "ar-table".
 */

import * as React from "react";

import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonBody,
} from "@/components/ui/table";
import { formatRelativeTime } from "@/lib/date/format-relative-time";
import { useGetProfile } from "@/lib/api/generated";
import type { SequenceResponse } from "@/lib/api/model/sequenceResponse";
import { cn } from "@/lib/utils";
import useIsMobile from "@/hooks/use-is-mobile";

import { TableRowContextMenu } from "@/components/ui/table-row-context-menu";

import {
  EnrollmentActionsContextMenuContent,
  EnrollmentRowActions,
  type EnrollmentRowActionsState,
} from "./actions-menu";
import { ENROLLMENT_STATUS_META } from "../../lib/status-meta";
import type { EnrollmentListItem, EnrollmentStatus } from "../../lib/types";

const STEP_TYPE_LABEL: Record<string, string> = {
  automatic_email: "Auto Email",
  manual_email: "Manual Email",
  phone_call: "Phone Call",
  text_message: "Text Message",
  custom_task: "Custom Task",
  linkedin_profile_view: "Social View",
  linkedin_connection_request: "Social Connect",
  linkedin_message: "Social Message",
};

const TERMINAL_NEXT_DUE: Partial<
  Record<
    EnrollmentStatus,
    { label: string; tone: "muted" | "warning" | "danger" }
  >
> = {
  completed: { label: "Completed", tone: "muted" },
  cancelled: { label: "Cancelled", tone: "muted" },
  bounced: { label: "Bounced", tone: "danger" },
  error: { label: "Error", tone: "danger" },
};

interface EnrollmentTableProps {
  rows: EnrollmentListItem[];
  isLoading?: boolean;
  /** The sequence's current step set, used to resolve a friendly
   *  "Step N+1 of M: Type" label for each row. Undefined while the
   *  parent's `useGetSequence` is still loading — callers should still
   *  render the table; the cell falls back to a numeric label. */
  sequence?: SequenceResponse | null;
  /** Distinguishes "no rows yet" from "filters returned nothing". */
  emptyVariant?: "no-enrollments" | "no-results";
  /** Now-injection seam for deterministic relative-time formatting in
   *  tests. */
  now?: Date;
  /** Row click → opens the per-enrollment detail drawer (Task 58 wires
   *  this up). Optional so the table works in standalone tests. */
  onRowClick?: (enrollment: EnrollmentListItem) => void;
  /** Inserted into each row's trailing cell. Task 57 passes the
   *  Pause/Resume/Cancel/Restart actions menu. Receives shared row action
   *  state when the table also exposes a context menu. */
  renderActions?: (
    enrollment: EnrollmentListItem,
    actions?: EnrollmentRowActionsState,
  ) => React.ReactNode;
  jobId?: string;
  sequenceId?: string;
}

const COLUMN_COUNT = 5;

/**
 * Profile column — fetches the profile lazily so the table doesn't
 * fan out 25 requests on first render. The cell renders an avatar
 * shell + skeleton text while loading and gracefully falls back to
 * "Unknown candidate" if the profile is missing (e.g. soft-deleted).
 */
function ProfileCell({ profileId }: { profileId: string }) {
  const profile = useGetProfile(profileId, undefined, {
    query: {
      enabled: Boolean(profileId),
      staleTime: 60_000,
    },
  });

  if (profile.isLoading) {
    return (
      <div
        data-testid={`enrollment-row-profile-loading-${profileId}`}
        className="flex items-center gap-2.5"
      >
        <div className="size-7 shrink-0 shimmer shimmer-bg text-foreground rounded-full bg-contrast/6" />
        <div className="h-3.5 w-32 shimmer shimmer-bg text-foreground rounded bg-contrast/6" />
      </div>
    );
  }

  const fullName = profile.data?.full_name?.trim() || "Unknown candidate";
  const headline =
    profile.data?.headline?.trim() || profile.data?.job_title?.trim() || null;

  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <CandidateAvatar
        name={fullName}
        avatarUrl={profile.data?.avatar_url ?? null}
        size={28}
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-heading">
          {fullName}
        </span>
        {headline && (
          <span className="truncate text-xs text-muted-foreground">
            {headline}
          </span>
        )}
      </div>
    </div>
  );
}

function StatusCell({ status }: { status: EnrollmentStatus }) {
  const meta = ENROLLMENT_STATUS_META[status];
  return (
    <span
      data-testid={`enrollment-row-status-${status}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-white/4 px-2 py-0.5 text-xs font-medium",
        meta.color,
      )}
    >
      <Icon name={meta.icon} size={12} />
      <span>{meta.label}</span>
    </span>
  );
}

function CurrentStepCell({
  enrollment,
  sequence,
}: {
  enrollment: EnrollmentListItem;
  sequence?: SequenceResponse | null;
}) {
  const steps = sequence?.current_step_set?.steps ?? [];
  const total = steps.length;
  const stepNumber = enrollment.current_step_index + 1;
  const stepType = steps[enrollment.current_step_index]?.type;
  const typeLabel = stepType ? STEP_TYPE_LABEL[stepType] : null;

  if (total === 0) {
    return (
      <span className="text-sm text-muted-foreground">Step {stepNumber}</span>
    );
  }

  return (
    <div className="flex flex-col text-sm">
      <span className="text-foreground">
        Step {stepNumber} of {total}
      </span>
      {typeLabel && (
        <span className="text-xs text-muted-foreground">{typeLabel}</span>
      )}
    </div>
  );
}

function NextDueCell({
  enrollment,
  now,
}: {
  enrollment: EnrollmentListItem;
  now: Date;
}) {
  const terminal = TERMINAL_NEXT_DUE[enrollment.status];
  if (terminal) {
    return (
      <span
        className={cn(
          "text-sm",
          terminal.tone === "danger" && "text-destructive",
          terminal.tone === "muted" && "text-muted-foreground",
        )}
      >
        {terminal.label}
      </span>
    );
  }

  if (enrollment.status === "paused") {
    return <span className="text-sm text-amber-500">Paused</span>;
  }

  if (!enrollment.next_due_at) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  return (
    <span className="text-sm text-foreground">
      {formatRelativeTime(enrollment.next_due_at, now)}
    </span>
  );
}

function CardMetaItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-contrast/40">
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function EnrollmentCard({
  enrollment,
  sequence,
  now,
  onRowClick,
  renderActions,
  jobId,
  sequenceId,
}: {
  enrollment: EnrollmentListItem;
  sequence?: SequenceResponse | null;
  now: Date;
  onRowClick?: (enrollment: EnrollmentListItem) => void;
  renderActions?: (
    enrollment: EnrollmentListItem,
    actions?: EnrollmentRowActionsState,
  ) => React.ReactNode;
  jobId?: string;
  sequenceId?: string;
}) {
  const actionsSlot =
    renderActions && jobId && sequenceId ? (
      <EnrollmentRowActions
        enrollment={enrollment}
        jobId={jobId}
        sequenceId={sequenceId}
      >
        {(actions) => renderActions(enrollment, actions)}
      </EnrollmentRowActions>
    ) : renderActions ? (
      renderActions(enrollment)
    ) : null;

  return (
    <div
      data-testid={`enrollment-row-${enrollment.id}`}
      className="flex w-full flex-col gap-2.5 border-b border-border bg-surface-elevated px-4 py-3"
    >
      <button
        type="button"
        onClick={onRowClick ? () => onRowClick(enrollment) : undefined}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <ProfileCell profileId={enrollment.profile_id} />
        <StatusCell status={enrollment.status} />
      </button>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 pl-[37px]">
        <CardMetaItem label="Step">
          <CurrentStepCell enrollment={enrollment} sequence={sequence} />
        </CardMetaItem>
        <CardMetaItem label="Next due">
          <NextDueCell enrollment={enrollment} now={now} />
        </CardMetaItem>
      </div>
      {actionsSlot ? (
        <div
          className="flex justify-end pl-[37px]"
          onClick={(e) => e.stopPropagation()}
        >
          {actionsSlot}
        </div>
      ) : null}
    </div>
  );
}

export function EnrollmentTable({
  rows,
  isLoading = false,
  sequence,
  emptyVariant = "no-enrollments",
  now,
  onRowClick,
  renderActions,
  jobId,
  sequenceId,
}: EnrollmentTableProps) {
  const resolvedNow = now ?? new Date();
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <div
        aria-label="Loading enrollments"
        role="status"
        aria-live="polite"
        className="flex flex-1 flex-col"
      >
        {isMobile ? (
          <div className="flex flex-col">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col gap-2.5 border-b border-border bg-surface-elevated px-4 py-3"
              >
                <div className="flex items-center gap-2.5">
                  <div className="size-7 shrink-0 shimmer shimmer-bg text-foreground rounded-full bg-contrast/[0.06]" />
                  <div className="h-3.5 w-32 shimmer shimmer-bg text-foreground rounded bg-contrast/[0.06]" />
                </div>
                <div className="grid grid-cols-2 gap-3 pl-[37px]">
                  <div className="h-3 w-20 shimmer shimmer-bg text-foreground rounded bg-contrast/[0.06]" />
                  <div className="h-3 w-16 shimmer shimmer-bg text-foreground rounded bg-contrast/[0.06]" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Table variant="list" layout="fixed">
            <TableHeader sticky>
              <EnrollmentTableHeaderRow />
            </TableHeader>
            <TableSkeletonBody
              count={6}
              renderCells={() =>
                Array.from({ length: COLUMN_COUNT }).map((_, i) => (
                  <SkeletonBar key={i} index={i} />
                ))
              }
            />
          </Table>
        )}
      </div>
    );
  }

  if (rows.length === 0) {
    return <EnrollmentTableEmpty variant={emptyVariant} />;
  }

  if (isMobile) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto bg-surface-primary">
        {rows.map((enrollment) => (
          <EnrollmentCard
            key={enrollment.id}
            enrollment={enrollment}
            sequence={sequence}
            now={resolvedNow}
            onRowClick={onRowClick}
            renderActions={renderActions}
            jobId={jobId}
            sequenceId={sequenceId}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-surface-primary">
      <Table variant="list" layout="fixed">
        <TableHeader sticky>
          <EnrollmentTableHeaderRow />
        </TableHeader>
        <TableBody>
          {rows.map((enrollment) =>
            jobId && sequenceId ? (
              <EnrollmentRowActions
                key={enrollment.id}
                enrollment={enrollment}
                jobId={jobId}
                sequenceId={sequenceId}
              >
                {(actions) => (
                  <TableRowContextMenu
                    content={
                      <EnrollmentActionsContextMenuContent actions={actions} />
                    }
                  >
                    <TableRow
                      rowLabel={`Enrollment ${enrollment.id}`}
                      interactive={!!onRowClick}
                      onClick={
                        onRowClick ? () => onRowClick(enrollment) : undefined
                      }
                      role="row"
                      data-testid={`enrollment-row-${enrollment.id}`}
                      className="group/row"
                    >
                      <TableCell>
                        <ProfileCell profileId={enrollment.profile_id} />
                      </TableCell>
                      <TableCell className="w-36">
                        <StatusCell status={enrollment.status} />
                      </TableCell>
                      <TableCell className="w-44">
                        <CurrentStepCell
                          enrollment={enrollment}
                          sequence={sequence}
                        />
                      </TableCell>
                      <TableCell className="w-40">
                        <NextDueCell enrollment={enrollment} now={resolvedNow} />
                      </TableCell>
                      <TableCell className="w-24 last:px-3">
                        {renderActions
                          ? renderActions(enrollment, actions)
                          : null}
                      </TableCell>
                    </TableRow>
                  </TableRowContextMenu>
                )}
              </EnrollmentRowActions>
            ) : (
              <TableRow
                key={enrollment.id}
                rowLabel={`Enrollment ${enrollment.id}`}
                interactive={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(enrollment) : undefined}
                role="row"
                data-testid={`enrollment-row-${enrollment.id}`}
                className="group/row"
              >
                <TableCell>
                  <ProfileCell profileId={enrollment.profile_id} />
                </TableCell>
                <TableCell className="w-36">
                  <StatusCell status={enrollment.status} />
                </TableCell>
                <TableCell className="w-44">
                  <CurrentStepCell enrollment={enrollment} sequence={sequence} />
                </TableCell>
                <TableCell className="w-40">
                  <NextDueCell enrollment={enrollment} now={resolvedNow} />
                </TableCell>
                <TableCell className="w-24 last:px-3">
                  {renderActions ? renderActions(enrollment) : null}
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function EnrollmentTableHeaderRow() {
  return (
    <TableRow>
      <TableHead>Profile</TableHead>
      <TableHead className="w-36">Status</TableHead>
      <TableHead className="w-44">Current step</TableHead>
      <TableHead className="w-40">Next due</TableHead>
      <TableHead className="w-24" aria-label="Actions" />
    </TableRow>
  );
}

function SkeletonBar({ index }: { index: number }) {
  const widths = ["72%", "48%", "60%", "55%", "30%"];
  return (
    <div
      className="h-3.5 shimmer shimmer-bg text-foreground rounded bg-contrast/6"
      style={{ width: widths[index % widths.length] }}
    />
  );
}

interface EnrollmentTableEmptyProps {
  variant: "no-enrollments" | "no-results";
}

function EnrollmentTableEmpty({ variant }: EnrollmentTableEmptyProps) {
  const copy =
    variant === "no-enrollments"
      ? {
          icon: "IconTeam" as IconName,
          title: "No enrollments yet",
          body: "Add candidates from this job's pipeline or talent search and click Enroll into Sequence.",
        }
      : {
          icon: "IconMagnifyingGlass" as IconName,
          title: "No enrollments match these filters",
          body: "Clear the status filter or pick a different one to see more rows.",
        };

  return (
    <div
      data-testid="enrollment-table-empty"
      data-variant={variant}
      className="flex flex-1 flex-col items-center justify-center gap-2 px-page py-16 text-center"
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-white/4">
        <Icon
          name={copy.icon}
          size={20}
          className="text-foreground opacity-70"
        />
      </div>
      <h3 className="text-base font-medium text-heading">{copy.title}</h3>
      <p className="max-w-sm text-sm text-muted-foreground">{copy.body}</p>
    </div>
  );
}
